/* ============================================================
   Events Calendar
   Initializes any .events-calendar-wrap sections on the page.
   Events are read from the embedded <script class="events-calendar-data">.
   ============================================================ */
(function () {
    'use strict';

    const MONTH_NAMES = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    // Parse 'YYYY-MM-DD' as LOCAL date. Avoids the common off-by-one where
    // new Date('2026-02-10') is interpreted as UTC midnight -> Feb 9 in PST.
    function parseDate(str) {
        if (!str) return null;
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    function stripTime(d) {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    function eventsOnDay(events, day) {
        const filtered = events.filter((e) => {
            const s = parseDate(e.startDate);
            const en = parseDate(e.endDate || e.startDate);
            return s && en && day >= stripTime(s) && day <= stripTime(en);
        });

        // Sort events: True priority first, then alphabetically by title
        return filtered.sort((a, b) => {
            return b.isPriority - a.isPriority || (a.title || '').localeCompare(b.title || '');
        });
    }

    // Expand each metaobject entry into one render-ready occurrence per date.
    function expandOccurrences(entries) {
        const out = [];
        (entries || []).forEach((entry) => {
            const list = Array.isArray(entry.dates)
                ? Array.from(new Set(entry.dates.filter(Boolean))).sort()
                : [];
            if (list.length) {
                list.forEach((d) => {
                    out.push(
                        Object.assign({}, entry, {
                            startDate: d,
                            endDate: d, // each recurring occurrence is a single day
                            dates: undefined
                        })
                    );
                });
                return;
            }
            if (entry.startDate) {
                out.push(
                    Object.assign({}, entry, {
                        endDate: entry.endDate || entry.startDate,
                        dates: undefined
                    })
                );
            }
        });
        return out;
    }

    class Calendar {
        constructor(section) {
            this.section = section;
            this.events = this.loadEvents();
            this.root = section.querySelector('.calendar');
            this.daysEl = this.root.querySelector('.days');
            this.titleEl = this.root.querySelector('.month-title');

            // Start on the nearest upcoming event, else today.
            const today = stripTime(new Date());
            const upcoming = this.events
                .map((e) => parseDate(e.startDate))
                .filter((d) => d && d >= today)
                .sort((a, b) => a - b)[0];
            const anchor = upcoming || today;
            this.year = anchor.getFullYear();
            this.month = anchor.getMonth();

            this.root.querySelector('.prev').addEventListener('click', () => this.shift(-1));
            this.root.querySelector('.next').addEventListener('click', () => this.shift(1));
            this.render();

            // View toggle
            section.querySelectorAll('.view-toggle button').forEach((btn) => {
                btn.addEventListener('click', () => {
                    this.switchView(btn);
                    // Reset list view to show ALL events if clicking the toggle manually
                    if (btn.dataset.view === 'list' && !btn.dataset.preventReset) {
                        this.renderList();
                    }
                });
            });

            this.renderList();
        }

        loadEvents() {
            const dataEl = this.section.querySelector('.events-calendar-data');
            if (!dataEl) return [];
            try {
                const raw = JSON.parse(dataEl.textContent) || [];
                return expandOccurrences(raw);
            } catch (err) {
                console.error('Events calendar: could not parse event data', err);
                return [];
            }
        }

        shift(delta) {
            this.month += delta;
            if (this.month < 0) {
                this.month = 11;
                this.year--;
            }
            if (this.month > 11) {
                this.month = 0;
                this.year++;
            }
            this.render();
        }

        render() {
            this.titleEl.textContent = `${this.year} ${MONTH_NAMES[this.month]}`;
            this.daysEl.innerHTML = '';

            const firstOfMonth = new Date(this.year, this.month, 1);
            const lastOfMonth = new Date(this.year, this.month + 1, 0);
            const mondayOffset = (firstOfMonth.getDay() + 6) % 7;

            for (let i = 0; i < mondayOffset; i++) {
                this.daysEl.appendChild(this.buildEmptyCell());
            }
            for (let d = 1; d <= lastOfMonth.getDate(); d++) {
                const date = new Date(this.year, this.month, d);
                this.daysEl.appendChild(this.buildDayCell(date, eventsOnDay(this.events, date)));
            }
            const totalCells = mondayOffset + lastOfMonth.getDate();
            const trailing = (7 - (totalCells % 7)) % 7;
            for (let i = 0; i < trailing; i++) {
                this.daysEl.appendChild(this.buildEmptyCell());
            }
        }

        buildEmptyCell() {
            const el = document.createElement('div');
            el.className = 'day empty';
            return el;
        }

        buildDayCell(date, events) {
            const el = document.createElement('div');
            el.className = 'day';
            const num = document.createElement('div');
            num.className = 'day-number';
            num.textContent = date.getDate();

            if (events.length) {
                el.classList.add('has-event');
                const ev = events[0]; // primary event (first by priority, then alphabetical)

                // Image treatment: inject <img> as background layer + has-image class.
                if (ev.image) {
                    el.classList.add('has-image');
                    const bg = document.createElement('img');
                    bg.className = 'event-bg';
                    bg.src = ev.image;
                    bg.alt = '';
                    bg.loading = 'lazy';
                    el.appendChild(bg);
                }

                el.appendChild(num);

                const t = document.createElement('div');
                t.className = 'event-title';
                t.textContent = ev.title;
                el.appendChild(t);

                // "+N more" indicator when there are additional events on this day.
                if (events.length > 1) {
                    const more = document.createElement('div');
                    more.className = 'event-more';
                    more.textContent = `+${events.length - 1} more`;
                    el.appendChild(more);
                }

                el.addEventListener('click', () => {
                    // Multi-event cells switch to list view filtered for this specific day
                    if (events.length > 1) {
                        const listBtn = this.section.querySelector(
                            '.view-toggle button[data-view="list"]'
                        );
                        if (listBtn) {
                            listBtn.dataset.preventReset = 'true'; // Stop normal tab click from wiping filter
                            listBtn.click(); // Switch UI to list tab
                            this.renderList(stripTime(date)); // Render filtered list
                            delete listBtn.dataset.preventReset; // Cleanup

                            // Scroll directly to the top of the list view container
                            const listWrap = this.section.querySelector('.list-view');
                            if (listWrap) {
                                listWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }
                        return;
                    }
                    if (ev.url && ev.url !== '#') window.location.href = ev.url;
                });
            } else {
                el.appendChild(num);
            }
            return el;
        }

        renderList(filterDate = null) {
            const wrap = this.section.querySelector('.list-view');
            if (!wrap) return;
            wrap.classList.toggle('day-filtered', Boolean(filterDate));
            const now = stripTime(new Date());

            const upcoming = [...this.events]
                .filter((e) => {
                    const start = parseDate(e.startDate);
                    const end = parseDate(e.endDate || e.startDate);
                    if (!start || !end) return false;

                    // If a specific date was clicked, filter to only events overlapping that day
                    if (filterDate) {
                        return filterDate >= stripTime(start) && filterDate <= stripTime(end);
                    }
                    // Otherwise, default behavior: show all upcoming events
                    return end >= now;
                })
                .sort((a, b) => {
                    const dateA = parseDate(a.startDate);
                    const dateB = parseDate(b.startDate);

                    // Sort by Date -> Priority -> Alphabetical
                    return (
                        dateA - dateB ||
                        b.isPriority - a.isPriority ||
                        (a.title || '').localeCompare(b.title || '')
                    );
                });

            // Clean up any previously appended "Show all events" button
            const existingActions = this.section.querySelector('.list-view-actions');
            if (existingActions) {
                existingActions.remove();
            }

            if (!upcoming.length) {
                wrap.innerHTML = '<div class="empty-state">No upcoming events</div>';
                return;
            }

            // Generate HTML for the list (only the list items)
            wrap.innerHTML = upcoming
                .map((e) => {
                    const d = parseDate(e.startDate);
                    const dateStr = `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
                    const thumb = e.image
                        ? `<img class="list-thumb" src="${escapeHtml(e.image)}" alt="" loading="lazy">`
                        : `<div class="list-thumb placeholder"></div>`;
                    return `
          <div class="list-item">
            ${thumb}
            <div>
              <div class="list-title">${escapeHtml(e.title)}</div>
              <div class="list-date-block"><div class="list-date">${dateStr}
                <add-to-calendar-button
                    name="${escapeHtml(e.title)}"
                    description="${escapeHtml(e.description)}"
                    startDate="${escapeHtml(e.startDate)}"
                    endDate="${escapeHtml(e.endDate)}"
                    startTime="${escapeHtml(e.startTime || '')}"
                    endTime="${escapeHtml(e.endTime || '23:59')}"
                    timeZone="America/Vancouver"
                    location="${escapeHtml(e.location || 'Yaletown')}"
                    options="'Apple','Google','iCal','Outlook.com'"
                    buttonStyle="text"
                    trigger="click"
                    hideIconButton
                    hideCheckmark
                    size="3"
                    >
                </add-to-calendar-button>
              </div></div>
              <div class="list-cta">
                <a class="link special-link" href="${e.url || '#'}">Read more</a>
              </div>
            </div>
          </div>`;
                })
                .join('');

            // If filtered, append the "Show all events" button immediately AFTER the wrap container
            if (filterDate) {
                const actionsWrap = document.createElement('div');
                actionsWrap.className = 'list-view-actions';
                actionsWrap.style.marginTop = '4.8rem';
                actionsWrap.style.textAlign = 'center';

                actionsWrap.innerHTML = `
                    <button class="btn button show-all-events-btn"
                    style="cursor: pointer;
                    padding: 6px 16px;
                    font-size: 1.2rem;">
                        Show all events
                    </button>
                `;

                // Insert right below the list container
                wrap.insertAdjacentElement('afterend', actionsWrap);

                // Bind click event to the newly created button
                const showAllBtn = actionsWrap.querySelector('.show-all-events-btn');
                showAllBtn.addEventListener('click', () => {
                    this.renderList(); // Render normal full list
                    // Scroll directly to the top of the list view container
                    wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
            }
        }

        switchView(btn) {
            const view = btn.dataset.view;
            this.section.querySelectorAll('.view-toggle button').forEach((b) => {
                b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
            });
            this.root.style.display = view === 'calendar' ? '' : 'none';
            this.section.querySelector('.list-view').classList.toggle('active', view === 'list');
        }
    }

    function escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = String(str || '');
        return div.innerHTML;
    }

    function init() {
        document.querySelectorAll('.events-calendar-wrap').forEach((section) => {
            if (!section.dataset.initialized) {
                new Calendar(section);
                section.dataset.initialized = 'true';
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-init on Shopify theme editor section events.
    document.addEventListener('shopify:section:load', init);
})();
